import { useState } from 'react';
import { MasteryVisualization } from './MasteryVisualization';
import { CardContent } from './study/CardContent';
import { AudioPlayer } from './AudioPlayer';
import { Database } from '../types/database';

type Card = Database['public']['Tables']['cards']['Row'];

interface CardDisplayProps {
  card: Card;
  showBack: boolean;
  onShowBack: () => void;
  onAction: (action: 'recalled' | 'missed' | 'mastered' | 'hidden') => void;
  userId?: string;
  currentMastery?: number;
  showMasteryVisualization?: boolean;
  actionButtons?: 'study' | 'sequential';
}

export function CardDisplay({
  card,
  showBack,
  onShowBack,
  onAction,
  userId,
  currentMastery = 0,
  showMasteryVisualization = true,
  actionButtons = 'study'
}: CardDisplayProps) {
  const [showTranslations, setShowTranslations] = useState(false);

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="space-y-6">
        {/* Mastery Progress Indicator */}
        {showMasteryVisualization && userId && (
          <MasteryVisualization
            userId={userId}
            cardId={card.id}
            currentMastery={currentMastery}
          />
        )}

        <div>
          <CardContent
            content={card.front}
            showTranslations={showTranslations}
            onToggleTranslations={() => setShowTranslations(!showTranslations)}
            enableTTSButtons={false}
          />
          {card.audio_url && (
            <div className="mt-4 flex items-center gap-2">
              <AudioPlayer audioUrl={card.audio_url} />
              <span className="text-xs text-gray-500">Press P to play/pause</span>
            </div>
          )}
        </div>

        {showBack && (
          <div className="border-t pt-6">
            <CardContent
              content={card.back}
              showTranslations={showTranslations}
              onToggleTranslations={() => setShowTranslations(!showTranslations)}
              enableTTSButtons={false}
            />
          </div>
        )}

        <div className="flex justify-center space-x-4 pt-4">
          {!showBack ? (
            <button
              onClick={onShowBack}
              className="px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
            >
              Show Answer
            </button>
          ) : (
            <>
              {actionButtons === 'study' ? (
                // Regular study mode buttons
                <>
                  <button
                    onClick={() => onAction('mastered')}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Mastered
                  </button>
                  <button
                    onClick={() => onAction('recalled')}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700"
                  >
                    Got it!
                  </button>
                  <button
                    onClick={() => onAction('missed')}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700"
                  >
                    Missed
                  </button>
                  <button
                    onClick={() => onAction('hidden')}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Hide
                  </button>
                </>
              ) : (
                // Sequential mode buttons
                <>
                  <button
                    onClick={() => onAction('recalled')}
                    className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
                  >
                    Got it! (Space)
                  </button>
                  <button
                    onClick={() => onAction('missed')}
                    className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium"
                  >
                    Missed (→)
                  </button>
                  <button
                    onClick={() => onAction('hidden')}
                    className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-medium"
                  >
                    Hide (H)
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
